import { useCallback, useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, Send, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// components
import { Button } from "@components/button/button";
import { LoadingFinalizeRecepcao } from "@components/loadings-skelleton/loading-finalize-recepcao";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

import { DeliveryOptions } from "@pages/admin/gerenciar-clientes/components/opcao-entrega";
import { handleMensagensPorTipoRecepcao } from "@pages/recepcao/helpers/messagem-por-tipo";

import { useBuscarPorEnderecosPorTipo } from "@/hooks/enderecos/buscar-por-enderecos-por-tipo";
// typings
import { IRecepcaoLiberarRemessaClientePayload } from "@/hooks/liberar-remessa/index.d";
// hooks / react-query
import { useRecepcaoLiberarRemessaCliente } from "@/hooks/liberar-remessa/recepcao-liberar-remessa-cliente";
// hooks / domain
import { useUpdateTarefa } from "@/hooks/minhas-tarefas/use-update-tarefa";
import { useStatusRemessa } from "@/hooks/remessas/status-remessa-do-cliente";
import { useBuscarTiposEntrega } from "@/hooks/tipos-entregas/buscar-tipos-entrega";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { notifyAlert } from "@/utils/notify-messages";
import { handleLabelTipoEntrega } from "@/utils/return-tipos-entrega";

// hooks / react-query
interface ICliente {
  cliente: {
    id: number;
    nome: string;
    email: string;
    cnpj: string;
    cpf: string;
    ativo: boolean;
    tipo_pessoa: string;
    telefone: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
  cliente_name: string;
  data: string;
  data_fim_producao: string;
  designer: any;
  hasMensagem: boolean;
  modelo: string;
  modelo_id: number;
  observacao: string | null;
  nome_identificacao: string;
  prazo: string;
  quantidadeSolicitacao: number;
  responsavel: string;
  status: string;
  tipo_entrega: any;
  liberacao_expedicao?: {
    tipo_entrega_id?: number;
    observacao?: string | null;
    outros?: string | null;
  } | null;
  remessa: number;
  numero_remessa: number;
}
type FinalizeExpeditionModalProps = {
  remessa: ICliente | null;
  // typeDelivery: string;
  handleClose: () => void;
};

type TiposEntregaSelecionado = {
  id: number;
  tipo: string;
  endereco_entrega_id?: number | null;
};

const schema = z.object({
  observacao: z.string().min(10, "A mensagem deve ter no mínimo 10 caracteres."),
  file: z.any().optional(), // novo campo
});

type FormData = z.infer<typeof schema>;

const FinalizeRecepcaoModal = ({ handleClose, remessa }: FinalizeExpeditionModalProps) => {
  const queryClient = useQueryClient(); // ← necessário para invalidar a query

  const { recepcaoLiberarRemessaClienteAsync } = useRecepcaoLiberarRemessaCliente();

  const [file, setFile] = useState<string | null>(null);

  const { user } = useStoreZustandUserAuth();

  const { enderecosTipos, isLoading: isLoadingEnderecos } = useBuscarPorEnderecosPorTipo(
    remessa?.cliente?.id ?? 1
  );

  const { tiposEntrega, isLoading: isLoadingTipos } = useBuscarTiposEntrega();

  // estado de carregamento inicial
  const isFetchingData = isLoadingEnderecos || isLoadingTipos;
  const { mutate: mutateUseUpdateTarefa, isPending } = useUpdateTarefa();
  const { statusRemessaAsync: solicitarRemessaAsync } = useStatusRemessa();

  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [dataApi, setDataApi] = useState<any>(null);
  const [outrosDetalhe, setOutrosDetalhe] = useState("");

  const outrosExpedicaoRaw = remessa?.liberacao_expedicao?.outros;
  const outrosExpedicao =
    outrosExpedicaoRaw === null || outrosExpedicaoRaw === undefined
      ? ""
      : String(outrosExpedicaoRaw).trim();
  const temOutrosExpedicao = outrosExpedicao.length > 0;

  const [tiposEntregaSelecionado, setTiposEntregaSelecionado] =
    useState<TiposEntregaSelecionado | null>(null); // Exemplo de tipo de entrega selecionado

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: FormData) => {
    if (!tiposEntregaSelecionado?.id) {
      notifyAlert("Verifique se você selecionou o tipo de entrega e tente novamente!");

      return;
    }

    setDataApi(data);
    setConfirmarEnvio(true);
  };

  const handleTipoEntregaChange = useCallback(
    (tipoSelecionado: TiposEntregaSelecionado) => {
      setTiposEntregaSelecionado(tipoSelecionado);
      if (tipoSelecionado.tipo !== "outros") {
        setOutrosDetalhe("");
        return;
      }
      setOutrosDetalhe(temOutrosExpedicao ? outrosExpedicao : "");
    },
    [outrosExpedicao, temOutrosExpedicao]
  );

  const confirmar = useCallback(async () => {
    const liberarRemessa = {
      remessa_id: remessa?.remessa,
      user_id_executor: user?.id || 0, // obtém o ID do usuário autenticado
      tipo_entrega_id: Number(tiposEntregaSelecionado?.id),
      file_path: dataApi.file[0] ?? null, // verifica se o arquivo foi selecionado
      observacao: dataApi.observacao,
      outros:
        tiposEntregaSelecionado?.tipo === "outros"
          ? temOutrosExpedicao
            ? outrosExpedicao
            : outrosDetalhe.trim() || null
          : null,
    } as IRecepcaoLiberarRemessaClientePayload;

    // executa a ação de envio
    const { data } = await recepcaoLiberarRemessaClienteAsync(liberarRemessa);

    if (data?.id) {
      mutateUseUpdateTarefa(
        {
          id_remessa: Number(remessa?.remessa),
          payload: {
            status: "concluido", // entregue status
            situacao: "concluido", // entregue status
          },
        },
        {
          onSuccess: async () => {
            await solicitarRemessaAsync({
              id_remessa: Number(remessa?.remessa),
              payload: { status: "concluido" },
            });

            await queryClient.invalidateQueries({ queryKey: ["recepcao"] });

            setConfirmarEnvio(false);
            handleClose();
          },
        }
      );

      return;
    }
  }, [
    dataApi,
    handleClose,
    outrosDetalhe,
    outrosExpedicao,
    temOutrosExpedicao,
    tiposEntregaSelecionado,
    remessa,
    recepcaoLiberarRemessaClienteAsync,
    mutateUseUpdateTarefa,
    user,
    solicitarRemessaAsync,
    queryClient,
  ]);

  useEffect(() => {
    const enderecoDelivery = enderecosTipos?.residencial;
    const tipo = (tiposEntregaSelecionado?.tipo || "").toLowerCase().trim();
    const nome = remessa?.cliente?.nome ?? "Cliente";
    const remessaId = remessa?.numero_remessa ?? 0;
    const data_fim_remessa = remessa?.data_fim_producao;
    const endereco = `${enderecoDelivery?.logradouro}, N° ${enderecoDelivery?.numero}, Bairro ${enderecoDelivery?.bairro}, CEP ${enderecoDelivery?.cep}.`;
    const responsavel = `${enderecoDelivery?.nome_responsavel}`;
    const setor = `${enderecoDelivery?.setor}`;

    if (handleMensagensPorTipoRecepcao[tipo]) {
      const msg = handleMensagensPorTipoRecepcao[tipo](
        nome,
        remessaId,
        endereco,
        responsavel,
        setor,
        data_fim_remessa!
      );
      setValue("observacao", msg, { shouldDirty: true, shouldValidate: true });
    }
  }, [tiposEntregaSelecionado, remessa, setValue, enderecosTipos]);

  useEffect(() => {
    if (!remessa) {
      return;
    }

    const tipoEntregaId =
      remessa?.liberacao_expedicao?.tipo_entrega_id ?? remessa?.tipo_entrega?.tipo_entrega_id;

    if (typeof tipoEntregaId !== "number" || !Number.isFinite(tipoEntregaId)) {
      setTiposEntregaSelecionado(null);
    } else {
      const tipoFromApi = tiposEntrega.find((item) => item.id === tipoEntregaId);

      if (tipoFromApi) {
        setTiposEntregaSelecionado({
          id: tipoFromApi.id,
          tipo: tipoFromApi.tipo,
        });
      } else {
        const tipo = handleLabelTipoEntrega(tipoEntregaId);
        setTiposEntregaSelecionado(tipo ? { id: tipoEntregaId, tipo } : null);
      }
    }

    setOutrosDetalhe(temOutrosExpedicao ? outrosExpedicao : "");
  }, [remessa, tiposEntrega, outrosExpedicao, temOutrosExpedicao]);

  return (
    <>
      {/* Modal principal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="max-h-[90vh] w-[700px] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="text-blue-600" size={24} />
              <h2 className="text-xl font-semibold text-zinc-800">Finalizar remessa do cliente</h2>
            </div>
            <button onClick={handleClose}>
              <X className="size-5 text-zinc-400" />
            </button>
          </div>

          {isFetchingData ? (
            <LoadingFinalizeRecepcao />
          ) : (
            <>
              {/* Formulário */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Mensagem do email */}
                <div>
                  <label className="mb-2 block text-zinc-800">Mensagem</label>
                  <textarea
                    {...register("observacao")}
                    rows={8}
                    placeholder="Digite a mensagem de liberação do pedido"
                    className="w-full rounded-lg border border-zinc-800 px-4 py-3 text-zinc-700 outline-none"
                  />
                  {errors.observacao && (
                    <span className="text-sm text-red-500">{errors.observacao.message}</span>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-zinc-800">Arquivo (PNG ou Excel)</label>

                  <div className="relative">
                    {/* Input real (escondido) */}
                    <input
                      id="file-upload"
                      type="file"
                      accept=".png, .xlsx, .xls"
                      {...register("file")}
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      onChange={(event) => setFile(event.target.value)}
                    />

                    {/* Botão falso estilizado */}
                    <label
                      htmlFor="file-upload"
                      className="block w-full cursor-pointer rounded-lg border border-zinc-800 bg-white px-4 py-2 text-center text-sm text-zinc-700 hover:bg-zinc-100"
                    >
                      Selecionar Arquivo
                    </label>
                  </div>

                  {file && (
                    <div className="mt-2 text-zinc-700">
                      <div className="mt-2 flex items-center gap-3 rounded-xl border border-green-400 bg-emerald-100 p-3">
                        <strong>Arquivo Selecionado: </strong>
                        <p>{file ?? "sem"}</p>
                      </div>

                      {/* preview se for PNG */}
                    </div>
                  )}
                </div>

                {/* Forma de envio */}
                <div className="mt-2 flex-row items-center rounded-md">
                  <DeliveryOptions
                    tiposEntrega={tiposEntrega}
                    tipoEntregaSelecionada={tiposEntregaSelecionado?.tipo ?? ""}
                    onTipoEntregaChange={handleTipoEntregaChange}
                    outrosTexto={temOutrosExpedicao ? outrosExpedicao : outrosDetalhe}
                    onOutrosChange={temOutrosExpedicao ? undefined : setOutrosDetalhe}
                    outrosSomenteLeitura={temOutrosExpedicao}
                  />
                </div>

                <Button type="submit" className="mt-2 w-full" disabled={isPending}>
                  <Send className="size-5" />
                  Liberar remessa cliente
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Modal de Confirmação */}
      {confirmarEnvio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-[400px] space-y-5 rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-zinc-800">
              Confirmar liberação da remessa {remessa?.numero_remessa}?
            </h3>
            <p className="text-sm text-zinc-600">
              Deseja realmente confirmar a liberação da remessa do cliente?
            </p>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                className="bg-zinc-300 text-zinc-800 hover:bg-zinc-400"
                onClick={() => setConfirmarEnvio(false)}
              >
                Cancelar
              </Button>
              <Button disabled={isPending} type="button" onClick={confirmar}>
                {isPending ? <SpinnerSimple /> : "Liberar remessa"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { FinalizeRecepcaoModal };
